import * as React from "react";
import {
  Users, Activity, LogIn, BarChart3, Clock, Loader2, TrendingUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Cell, PieChart, Pie
} from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MODULE_COLORS = {
  workload: "#f97316", country_sentinel: "#3b82f6", admin: "#8b5cf6",
  auth: "#64748b", anomaly: "#ec4899", percentile_analysis: "#06b6d4",
  qa_validation: "#14b8a6", anomaly_v2: "#ef4444", percentile_v2: "#0891b2",
  campaign_performance: "#4f46e5", budget_utilization: "#10b981",
  campaign_structure: "#6366f1", media_planner: "#7c3aed", master_dashboard: "#f59e0b",
};
const MODULE_LABELS = {
  workload: "Workload", country_sentinel: "Country Sentinel", admin: "Admin",
  auth: "Auth", anomaly: "Anomalías", percentile_analysis: "Percentiles",
  qa_validation: "QA", anomaly_v2: "Anomalías v2", percentile_v2: "Percentiles v2",
  campaign_performance: "Campaign Perf.", budget_utilization: "Budget Util.",
  campaign_structure: "Camp. Structure", media_planner: "Media Planner", master_dashboard: "Master Dashboard",
};

export const AdminDashboardAnalytics = () => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [period, setPeriod] = React.useState("month");

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const r = await axios.get(`${API}/admin/dashboard-analytics?period=${period}`, { withCredentials: true });
        setData(r.data);
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    })();
  }, [period]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return <p className="text-slate-400 text-center py-8">No hay datos disponibles</p>;

  const { summary: s, daily_activity, login_trend, by_module, by_action, by_hour, top_users } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-500" />
            Uso de la Plataforma
          </h2>
          <p className="text-sm text-slate-500 mt-1">Estadisticas globales de actividad</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">Ultima semana</SelectItem>
            <SelectItem value="month">Ultimo mes</SelectItem>
            <SelectItem value="quarter">Ultimo trimestre</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Actividades", value: s.total_activities, icon: Activity, color: "text-indigo-600" },
          { label: "Usuarios Activos", value: s.active_users, icon: Users, color: "text-emerald-600", sub: `de ${s.total_users} registrados` },
          { label: "Logins", value: s.total_logins, icon: LogIn, color: "text-blue-600" },
          { label: "Prom. Diario", value: s.avg_daily_activities, icon: TrendingUp, color: "text-amber-600" },
          { label: "Tasa Adopcion", value: s.total_users > 0 ? Math.round(s.active_users / s.total_users * 100) + "%" : "0%", icon: Users, color: "text-purple-600" },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] text-slate-400">{kpi.label}</p>
                <kpi.icon className="h-4 w-4 text-slate-300" />
              </div>
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              {kpi.sub && <p className="text-[10px] text-slate-400 mt-0.5">{kpi.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1: Activity Trend + Logins */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Actividad Diaria</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={daily_activity} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                <defs>
                  <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="usrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip />
                <Area type="monotone" dataKey="activities" name="Actividades" stroke="#6366f1" fill="url(#actGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="unique_users" name="Usuarios" stroke="#10b981" fill="url(#usrGrad)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Module Distribution Donut */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Uso por Modulo</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const pieData = by_module.map(m => ({
                name: MODULE_LABELS[m.module] || m.module,
                value: m.count,
                fill: MODULE_COLORS[m.module] || "#94a3b8"
              }));
              const total = pieData.reduce((a, d) => a + d.value, 0);
              return (
                <div>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2}>
                        {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-1">
                    {pieData.slice(0, 6).map(d => (
                      <span key={d.name} className="text-[10px] text-slate-500">
                        <span className="inline-block w-2 h-2 rounded-full mr-0.5" style={{ backgroundColor: d.fill }} />
                        {d.name} {total > 0 ? Math.round(d.value / total * 100) : 0}%
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Actions + Hours */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Top Actions */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm">Acciones Mas Frecuentes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={by_action.slice(0, 8)} layout="vertical" margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis dataKey="action" type="category" tick={{ fontSize: 10 }} width={90} />
                <RTooltip />
                <Bar dataKey="count" name="Cantidad" radius={[0, 4, 4, 0]} fill="#6366f1">
                  {by_action.slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#6366f1" : i < 3 ? "#818cf8" : "#c7d2fe"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Activity by Hour */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-1">
              <Clock className="h-4 w-4 text-slate-400" />
              Actividad por Hora del Dia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const allHours = Array.from({ length: 24 }, (_, i) => {
                const h = String(i).padStart(2, "0");
                const found = by_hour.find(x => x.hour === h);
                return { hour: h + ":00", count: found ? found.count : 0 };
              });
              return (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={allHours} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 9 }} interval={2} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RTooltip />
                    <Bar dataKey="count" name="Actividades" radius={[2, 2, 0, 0]}>
                      {allHours.map((d, i) => (
                        <Cell key={i} fill={d.count > 5 ? "#6366f1" : d.count > 0 ? "#a5b4fc" : "#e2e8f0"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Top Users */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Usuarios Mas Activos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {top_users.map((u, i) => {
              const maxAct = top_users[0]?.activities || 1;
              return (
                <div key={u.email} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                  <span className="text-xs font-mono text-slate-400 w-5 text-right">{i + 1}</span>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="h-8 w-8 rounded-full flex-shrink-0" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-indigo-600">{(u.name || u.email).charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate">{u.name || u.email}</p>
                      {u.role === "admin" && <Badge className="text-[9px] bg-purple-100 text-purple-700">admin</Badge>}
                    </div>
                    <p className="text-[11px] text-slate-400">{u.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0 w-32">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="bg-indigo-500 h-full rounded-full" style={{ width: `${(u.activities / maxAct) * 100}%` }} />
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-8 text-right">{u.activities}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">{u.modules_count} modulos</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
